import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Bluetooth,
  Briefcase,
  Building2,
  Check,
  Cpu,
  GraduationCap,
  Layers,
  Mail,
  MapPin,
  Network,
  Phone,
  RadioTower,
  ScanSearch,
  ShieldAlert,
  Signal,
  TrainFront,
  Unplug,
  Wallet,
  Warehouse,
  Wifi,
  X,
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Separator } from './components/ui/separator';
import { cn } from './lib/utils';

const EMAIL = 'hai@leocation.com';
const PHONE_DISPLAY = '052-689-1150';
const PHONE_HREF = 'tel:+972526891150';

const NAV = [
  { id: 'challenge', label: 'Challenge' },
  { id: 'solution', label: 'Solution' },
  { id: 'proof', label: 'Field Test' },
  { id: 'team', label: 'Team' },
];

const AFFECTED = [
  { icon: Warehouse, title: 'Indoor', text: 'Satellite signals don’t reach inside buildings, tunnels and parking structures.' },
  { icon: Building2, title: 'Urban canyon', text: 'Dense city blocks reflect and block GPS, pushing errors to tens of meters.' },
  { icon: TrainFront, title: 'Underground', text: 'Metro systems and underground facilities have no sky view at all.' },
  { icon: ShieldAlert, title: 'Jamming', text: 'Intentional interference and spoofing shift or erase your position entirely.' },
];

const LAYERS = [
  { icon: Signal, label: 'Cellular' },
  { icon: Wifi, label: 'Wi-Fi' },
  { icon: Bluetooth, label: 'Bluetooth' },
];

const STEPS = [
  { icon: RadioTower, title: 'Detect', text: 'The system detects the RF transmitters in the surrounding environment.' },
  { icon: ScanSearch, title: 'Identify', text: 'It identifies the source of each signal - independent of satellites.' },
  { icon: MapPin, title: 'Locate', text: 'It calculates the user’s location and streams it to any GIS platform.' },
];

const EDGE = [
  { icon: Unplug, title: 'No new infrastructure', text: 'Runs on the RF networks already deployed around you.' },
  { icon: Network, title: 'No carrier in the process', text: 'Works without agreements or integration with cellular operators.' },
  { icon: Wallet, title: 'Low cost', text: 'Software-first approach keeps deployment and per-user cost low.' },
  { icon: Layers, title: 'GIS-agnostic', text: 'Plugs into any GIS or command-and-control platform.' },
];

const TEAM = [
  { icon: Cpu, title: 'Spectrum & Mission-Critical Systems', text: 'Full-stack hardware & software engineering, with a background in an Israeli Air Force technology unit focused on spectrum and mission-critical systems.' },
  { icon: GraduationCap, title: 'Positioning Science', text: 'PhD-level expertise in positioning, mapping and navigation algorithms - the science behind turning RF signals into location.' },
  { icon: Briefcase, title: 'Business & Partnerships', text: 'Business development, strategic partnerships and go-to-market.' },
];

function LogoMark({ white = false, className }) {
  return (
    <img
      src={white ? '/logo-mark-white.png' : '/logo-mark.png'}
      alt=""
      aria-hidden="true"
      className={cn('h-7 w-auto', className)}
    />
  );
}

function Wordmark({ white = false }) {
  return (
    <span className={cn('flex items-center gap-2.5 text-lg font-semibold tracking-tight', white ? 'text-white' : 'text-primary')}>
      <LogoMark white={white} />
      Leocation
    </span>
  );
}

function SectionHeader({ eyebrow, title, description, dark = false }) {
  return (
    <div data-reveal className="mx-auto mb-12 max-w-2xl text-center">
      <Badge variant="outline" className={cn('mb-4', dark && 'border-white/20 text-white/80')}>
        {eyebrow}
      </Badge>
      <h2 className={cn('text-3xl font-semibold tracking-tight sm:text-4xl', dark ? 'text-white' : 'text-foreground')}>
        {title}
      </h2>
      {description && (
        <p className={cn('mt-4 text-lg', dark ? 'text-white/60' : 'text-muted-foreground')}>{description}</p>
      )}
    </div>
  );
}

function Radar() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[420px]">
      {[0, 16, 32, 48].map((inset) => (
        <span
          key={inset}
          className="absolute rounded-full border border-white/15"
          style={{ inset: `${inset}%` }}
        />
      ))}
      <span className="absolute inset-0 animate-sweep rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,transparent_300deg,rgba(255,255,255,0.18)_360deg)]" />
      <span className="absolute left-1/2 top-0 h-full w-px bg-white/10" />
      <span className="absolute left-0 top-1/2 h-px w-full bg-white/10" />

      <span className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2">
        <span className="absolute inline-flex h-4 w-4 animate-ping rounded-full bg-brand-red opacity-60" />
        <span className="relative inline-flex h-4 w-4 rounded-full bg-brand-red ring-4 ring-brand-red/25" />
      </span>

      {[
        { icon: Signal, cls: 'left-[14%] top-[22%]' },
        { icon: Wifi, cls: 'right-[12%] top-[30%]' },
        { icon: Bluetooth, cls: 'bottom-[16%] left-[30%]' },
      ].map(({ icon: Icon, cls }, i) => (
        <span
          key={i}
          className={cn('absolute flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur', cls)}
        >
          <Icon className="h-4 w-4" />
        </span>
      ))}

      <Card className="absolute -bottom-2 right-0 w-56 border-white/10 bg-white/95 shadow-xl backdrop-blur">
        <CardContent className="space-y-2 p-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">GPS</span>
            <span className="flex items-center gap-1 font-medium text-brand-red">
              <X className="h-3.5 w-3.5" /> Amman
            </span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Leocation</span>
            <span className="flex items-center gap-1 font-medium text-primary">
              <Check className="h-3.5 w-3.5" /> Tel Aviv
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SignalStatus({ className }) {
  return (
    <div className={cn('flex items-center gap-4 rounded-lg border border-white/10 bg-white/5 p-4 backdrop-blur', className)}>
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full border border-white/15">
        <span className="absolute inset-[22%] rounded-full border border-white/15" />
        <span className="absolute inset-0 animate-sweep rounded-full bg-[conic-gradient(from_0deg,transparent_0deg,transparent_270deg,rgba(255,255,255,0.25)_360deg)]" />
        <span className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2">
          <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-brand-red opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-red" />
        </span>
      </div>
      <div className="min-w-0 flex-1 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-white/50">GPS</span>
          <span className="flex items-center gap-1 font-medium text-brand-red">
            <X className="h-3.5 w-3.5" /> Amman
          </span>
        </div>
        <Separator className="bg-white/10" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-white/50">Leocation</span>
          <span className="flex items-center gap-1 font-medium text-white">
            <Check className="h-3.5 w-3.5" /> Tel Aviv
          </span>
        </div>
      </div>
    </div>
  );
}

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal], [data-section]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.hasAttribute('data-reveal')) {
            const index = Array.prototype.indexOf.call(el.parentElement.children, el);
            el.style.transitionDelay = `${Math.min(index, 6) * 90}ms`;
          }
          el.classList.add('is-visible');
          observer.unobserve(el);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function ScrollProgress() {
  const barRef = useRef(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (barRef.current) barRef.current.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden">
      <div ref={barRef} className="h-full origin-left scale-x-0 bg-gradient-to-r from-white/60 to-brand-red" />
    </div>
  );
}

export default function App() {
  const [status, setStatus] = useState('idle');
  useScrollReveal();

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    setTimeout(() => {
      e.target.reset();
      setStatus('sent');
      setTimeout(() => setStatus('idle'), 5000);
    }, 1200);
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-brand-ink">
        <div className="container flex h-16 items-center justify-between">
          <a href="#top" aria-label="Leocation home">
            <Wordmark white />
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((item) => (
              <a key={item.id} href={`#${item.id}`} className="text-sm text-white/70 transition-colors hover:text-white">
                {item.label}
              </a>
            ))}
          </nav>
          <Button asChild size="sm" className="bg-white text-primary hover:bg-white/90">
            <a href="#contact">Contact us</a>
          </Button>
        </div>
        <ScrollProgress />
      </header>

      <main id="top">
        {/* Hero */}
        <section className="relative overflow-hidden bg-brand-ink text-white">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
          <div className="container relative grid items-center gap-16 py-14 sm:py-20 lg:grid-cols-[1.15fr_1fr] lg:py-28">
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="mb-6 flex flex-wrap gap-2">
                <Badge variant="outline" className="border-white/20 bg-white/5 text-white/80">MVP</Badge>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-white/80">3 pilots in the field</Badge>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-white/80">Starburst Accelerator</Badge>
              </div>
              <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-[3.5rem]">
                When GPS goes dark,
                <br />
                <span className="text-white/60">we don’t.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-white/70">
                Leocation adds RF positioning layers beyond GPS - turning the cellular, Wi-Fi and Bluetooth
                signals already around you into a location you can trust.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                  <a href="#contact">
                    Start a pilot <ArrowRight />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                  <a href="#solution">How it works</a>
                </Button>
              </div>
              <SignalStatus className="mt-10 lg:hidden" />
              <p className="mt-10 text-sm font-medium tracking-wide text-white/40">
                Unjammable · Unstoppable · Leo.
              </p>
            </div>
            <div className="hidden animate-in fade-in zoom-in-95 duration-1000 lg:block">
              <Radar />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="border-b bg-background">
          <div className="container grid grid-cols-1 divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {[
              { value: '~$1B', label: 'Loss from one day without GPS in the U.S.' },
              { value: '3', label: 'Pilots in the field' },
              { value: '0', label: 'New infrastructure required' },
            ].map((s) => (
              <div key={s.label} className="px-6 py-8 text-center">
                <div className="text-3xl font-semibold tracking-tight text-primary">{s.value}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Challenge */}
        <section id="challenge" className="scroll-mt-16 py-24">
          <div className="container">
            <SectionHeader
              eyebrow="The Challenge"
              title="GPS is no longer reliable."
              description="Every day, critical operations lose their position in the places where it matters most."
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {AFFECTED.map(({ icon: Icon, title, text }) => (
                <Card data-reveal key={title} className="hover:shadow-md">
                  <CardHeader>
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription className="leading-relaxed">{text}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
            <Card data-reveal className="mx-auto mt-10 max-w-3xl border-l-4 border-l-brand-red bg-secondary/50">
              <CardContent className="p-6">
                <blockquote className="text-lg font-medium leading-relaxed text-foreground">
                  “Disruption to GPS has the potential to undermine the nation’s economic and national security,
                  and the risks to our current system are only increasing.”
                </blockquote>
                <p className="mt-3 text-sm text-muted-foreground">Brendan Carr, Chairman of the FCC (2025)</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Solution */}
        <section data-section id="solution" className="scroll-mt-16 border-y bg-secondary/40 py-24">
          <div className="container">
            <SectionHeader
              eyebrow="The Solution"
              title="RF positioning layers beyond GPS."
              description="RF signals are everywhere. We turn them into navigation."
            />
            <div data-reveal className="mb-10 flex flex-wrap items-center justify-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">MVP RF layers:</span>
              {LAYERS.map(({ icon: Icon, label }) => (
                <Badge key={label} variant="secondary" className="gap-1.5 border bg-background px-3 py-1 text-sm font-medium">
                  <Icon className="h-3.5 w-3.5" /> {label}
                </Badge>
              ))}
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <Card data-reveal key={title} className="relative">
                  <CardHeader>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-mono text-sm text-muted-foreground">0{i + 1}</span>
                    </div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription className="leading-relaxed">{text}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Field test */}
        <section data-section id="proof" className="scroll-mt-16 bg-brand-ink py-24 text-white">
          <div className="container">
            <SectionHeader
              dark
              eyebrow="Proven in the Field"
              title="What we predicted would happen, happened exactly."
              description="Real-world test during the Iran–Israel conflict · July 12, 2026"
            />
            <div className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2">
              <Card data-reveal className="border-white/10 bg-white/5 text-white">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardDescription className="text-white/50">GPS reported</CardDescription>
                    <Badge className="bg-brand-red text-white hover:bg-brand-red">
                      <X className="h-3 w-3" /> Wrong
                    </Badge>
                  </div>
                  <CardTitle className="pt-2 text-3xl">Amman, Jordan</CardTitle>
                </CardHeader>
                <CardContent className="text-white/60">Over 120 km away from our real position.</CardContent>
              </Card>
              <Card data-reveal className="border-white/10 bg-white text-foreground">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardDescription>Leocation reported</CardDescription>
                    <Badge>
                      <Check className="h-3 w-3" /> Correct
                    </Badge>
                  </div>
                  <CardTitle className="pt-2 text-3xl">Tel Aviv, Israel</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">Our true location - Midtown commercial center.</CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Edge */}
        <section className="py-24">
          <div className="container">
            <SectionHeader eyebrow="Competitive Edge" title="Where we win." />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {EDGE.map(({ icon: Icon, title, text }) => (
                <Card data-reveal key={title}>
                  <CardHeader>
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription className="leading-relaxed">{text}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section data-section id="team" className="scroll-mt-16 border-y bg-secondary/40 py-24">
          <div className="container">
            <SectionHeader
              eyebrow="The Team"
              title="Built by RF & system engineers."
              description="Deep RF and positioning expertise, combined with full-stack hardware, software and business experience."
            />
            <div className="grid gap-4 md:grid-cols-3">
              {TEAM.map(({ icon: Icon, title, text }) => (
                <Card data-reveal key={title}>
                  <CardHeader>
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <CardDescription className="leading-relaxed">{text}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="scroll-mt-16 py-24">
          <div className="container grid items-center gap-12 lg:grid-cols-2">
            <div data-reveal>
              <Badge variant="outline" className="mb-4">Contact</Badge>
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Let’s keep you positioned.
              </h2>
              <p className="mt-4 max-w-md text-lg text-muted-foreground">
                We’re looking for pilot partners and early customers in public safety and transportation.
              </p>
              <div className="mt-8 space-y-3">
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-foreground hover:text-primary">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md border">
                    <Mail className="h-4 w-4" />
                  </span>
                  <span className="font-medium">{EMAIL}</span>
                </a>
                <a href={PHONE_HREF} className="flex items-center gap-3 text-foreground hover:text-primary">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md border">
                    <Phone className="h-4 w-4" />
                  </span>
                  <span className="font-medium">{PHONE_DISPLAY}</span>
                </a>
              </div>
            </div>

            <Card data-reveal className="shadow-lg">
              <CardHeader>
                <CardTitle>Request a briefing</CardTitle>
                <CardDescription>Tell us a bit about your organization and we’ll get back to you.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name / Organization</Label>
                    <Input id="name" required placeholder="Jane Doe / Acme Transit" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Work email</Label>
                    <Input id="email" type="email" required placeholder="jane@organization.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input id="role" placeholder="Operations Manager, Mission Planner…" />
                  </div>
                  <Button type="submit" className="w-full" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Sending…' : 'Request a briefing'}
                  </Button>
                  {status === 'sent' && (
                    <p className="text-center text-sm font-medium text-primary">
                      Request received - we’ll be in touch shortly.
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <footer className="bg-brand-ink py-10 text-white">
        <div className="container flex flex-col items-center justify-between gap-4 sm:flex-row">
          <Wordmark white />
          <p className="text-sm text-white/50">
            © {new Date().getFullYear()} Leocation · Unjammable. Unstoppable. Leo.
          </p>
        </div>
      </footer>
    </div>
  );
}
